import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { Quote, QuoteLineItem } from '../types/myaccount';
import MyAccountPageShell from './MyAccountPageShell';
import { LoadingBar, SummaryCardsSkeleton } from './MyAccountLoadingStates';
import {
    canQuoteBeEdited,
    canQuoteProceedToCheckout,
    getQuoteDisplayId,
    getQuoteDisplayStatus,
    getQuotePrimaryActionLabel,
} from './quoteDisplay';
import { getStatusBadgeClassName } from '../utils/statusBadges';

interface ViewQuoteProps {
    wekaPagePermissions?: Record<string, boolean>;
}

const ViewQuote: React.FC<ViewQuoteProps> = ({ wekaPagePermissions = {} }) => {
    const { id } = useParams<{ id: string }>();
    const [quote, setQuote] = useState<Quote | null>(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [completing, setCompleting] = useState(false);
    const canEditQuotePermission = wekaPagePermissions.edit_quote !== false;
    const canCheckoutQuotePermission = wekaPagePermissions.checkout_quote !== false;

    useEffect(() => {
        if (id) {
            loadQuote(parseInt(id));
        }
    }, [id]);

    const loadQuote = async (quoteId: number) => {
        try {
            setLoading(true);
            setError(null);

            const response = await fetch(`/wp-json/weka/v1/quotes/${quoteId}`, {
                headers: {
                    'X-WP-Nonce': (window as any).wekaApiSettings?.nonce || '',
                },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error('Failed to load quote');
            }

            const data = await response.json();

            if (data.success) {
                setQuote(data.quote);
            } else {
                throw new Error(data.error || 'Failed to load quote');
            }
        } catch (err) {
            console.error('Error loading quote:', err);
            setError(err instanceof Error ? err.message : 'An error occurred');
        } finally {
            setLoading(false);
        }
    };

    const handlePayQuote = async () => {
        if (!quote) return;
        if (!canCheckoutQuotePermission) {
            alert('Quote checkout is disabled for this page.');
            return;
        }

        if (quote.has_active_checkout_order && quote.converted_order_pay_url) {
            window.location.href = quote.converted_order_pay_url;
            return;
        }

        if (!confirm('Convert this quote to an order and continue to payment?')) {
            return;
        }

        try {
            setCompleting(true);

            const response = await fetch(`/wp-json/weka/v1/quotes/${quote.id}/checkout-order`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'X-WP-Nonce': (window as any).wekaApiSettings?.nonce || '',
                },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error('Failed to prepare quote payment');
            }

            const data = await response.json();

            if (data.success) {
                window.location.href = data.order_pay_url || data.order_view_url || '/my-account/orders';
            } else {
                throw new Error(data.error || 'Failed to prepare quote payment');
            }
        } catch (err) {
            console.error('Error preparing quote payment:', err);
            alert(err instanceof Error ? err.message : 'Failed to prepare quote payment');
        } finally {
            setCompleting(false);
        }
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric' 
        });
    };

    const formatCurrency = (amount: string | number | undefined) => {
        if (!amount) return '$0.00';
        const num = typeof amount === 'string' ? parseFloat(amount) : amount;
        return `$${num.toFixed(2)}`;
    };

    const formatAddressLines = () => {
        if (!quote?.shipping) {
            return [];
        }

        const shipping = quote.shipping;
        const name = [shipping.first_name, shipping.last_name].filter(Boolean).join(' ').trim();
        const locality = [shipping.city, shipping.state, shipping.postcode].filter(Boolean).join(', ').trim();

        return [
            name,
            shipping.company || '',
            shipping.address_1 || '',
            shipping.address_2 || '',
            locality,
            shipping.country || '',
            shipping.phone || '',
        ].filter((value) => value !== '');
    };

    const formatPickupLines = () => {
        if (!quote?.pickup_store) {
            return [];
        }

        return [
            quote.pickup_store.name || '',
            quote.pickup_store.address || '',
            quote.pickup_store.phone || '',
            quote.pickup_store.opening_hours || '',
        ].filter((value) => value !== '');
    };

    const getStatusLabel = (status: string) => {
        if (status === 'completed' || status === 'quote-completed') {
            return 'Completed';
        }

        if (status === 'awaiting-payment') {
            return 'Awaiting Payment';
        }

        if (status === 'converted') {
            return 'Converted';
        }

        if (status === 'approved') {
            return 'Pending';
        }

        return status.replace('-', ' ').replace(/\b\w/g, l => l.toUpperCase());
    };

    const calculateSubtotal = () => {
        if (!quote) return 0;
        if (quote.subtotal) return parseFloat(quote.subtotal);
        if (quote.total) {
            const total = parseFloat(quote.total);
            const tax = parseFloat(quote.tax || '0');
            const discount = parseFloat(quote.discount || '0');
            const shipping = parseFloat(quote.shipping_total || '0');
            return total - tax - shipping + discount;
        }
        return 0;
    };

    if (loading) {
        return (
            <MyAccountPageShell className="view-quote">
                <div className="row mb-4">
                    <div className="col-md-8">
                        <LoadingBar height="32px" width="132px" style={{ borderRadius: '8px', marginBottom: '16px' }} />
                        <LoadingBar height="26px" width="42%" style={{ marginBottom: '10px' }} />
                        <LoadingBar height="16px" width="28%" style={{ marginBottom: '8px' }} />
                        <LoadingBar height="16px" width="24%" style={{ marginBottom: '8px' }} />
                        <LoadingBar height="16px" width="18%" />
                    </div>
                    <div className="col-md-4">
                        <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
                            <LoadingBar height="16px" width="62%" style={{ marginBottom: '12px' }} />
                            <LoadingBar height="16px" width="58%" style={{ marginBottom: '12px' }} />
                            <LoadingBar height="16px" width="54%" style={{ marginBottom: '12px' }} />
                            <LoadingBar height="1px" width="100%" style={{ margin: '12px 0', opacity: 0.35 }} />
                            <LoadingBar height="26px" width="72%" />
                        </div>
                    </div>
                </div>
                <div className="mb-4 pb-4 border-bottom">
                    <LoadingBar height="20px" width="18%" style={{ marginBottom: '16px' }} />
                    <SummaryCardsSkeleton count={2} minHeight="52px" />
                </div>
                <div className="mb-4 pb-4 border-bottom">
                    <LoadingBar height="20px" width="16%" style={{ marginBottom: '16px' }} />
                    <div className="skeleton" style={{ height: '220px', borderRadius: '10px' }} />
                </div>
                <div>
                    <LoadingBar height="20px" width="14%" style={{ marginBottom: '16px' }} />
                    <div className="skeleton" style={{ height: '280px', borderRadius: '10px' }} />
                </div>
            </MyAccountPageShell>
        );
    }

    if (error || !quote) {
        return (
            <MyAccountPageShell
                title="Quote Details"
                subtitle="Review quote status, totals, and line items."
            >
                    <div className="alert alert-danger mb-3">
                        <i className="fas fa-exclamation-circle me-2"></i>
                        {error || 'Quote not found'}
                    </div>
                    <Link to="/quotes" className="button">
                        <i className="fas fa-arrow-left me-2"></i>
                        Back to Quotes
                    </Link>
            </MyAccountPageShell>
        );
    }

    const displayStatus = getQuoteDisplayStatus(quote);
    const canEdit = canEditQuotePermission && canQuoteBeEdited(quote);
    const canPayQuote = canCheckoutQuotePermission && canQuoteProceedToCheckout(quote);
    const hasLinkedOrder = Boolean(quote.converted_order_id && quote.converted_order_view_url);
    const shippingAddressLines = formatAddressLines();
    const pickupLines = formatPickupLines();
    const conversionBlockedMessage = !quote.converted_order_pay_url
        ? String(quote.conversion_blocker_message || '').trim()
        : '';

    return (
        <MyAccountPageShell className="view-quote">
                    {/* Header Section */}
                    <div className="row mb-4">
                        <div className="col-md-8">
                            <div className="mb-3">
                                <Link to="/quotes" className="button mb-3">
                                    <i className="fas fa-arrow-left me-2"></i>
                                    Back to Quotes
                                </Link>
                            </div>
                            <h4 className="mb-2">{quote.title}</h4>
                            <div className="text-muted">
                                <strong>Quote #:</strong> {getQuoteDisplayId(quote)}
                            </div>
                            <div className="text-muted">
                                <strong>Created:</strong> {formatDate(quote.date_created)}
                            </div>
                            <div className="mt-2">
                                <strong>Status:</strong>{' '}
                                <span className={getStatusBadgeClassName('quotes', displayStatus)}>
                                    {getStatusLabel(displayStatus)}
                                </span>
                            </div>
                            {quote.erp_quote_id && (
                                <div className="text-muted mt-2">
                                    <strong>ERP Quote ID:</strong> {quote.erp_quote_id}
                                </div>
                            )}
                            {displayStatus === 'awaiting-payment' && quote.converted_order_id && (
                                <div className="alert alert-info mt-3 mb-0 py-2">
                                    Payment is waiting on linked order #{quote.converted_order_id}.
                                </div>
                            )}
                            {displayStatus === 'converted' && quote.converted_order_id && (
                                <div className="alert alert-secondary mt-3 mb-0 py-2">
                                    This quote has been converted to linked order #{quote.converted_order_id}.
                                </div>
                            )}
                            {conversionBlockedMessage !== '' && !hasLinkedOrder && (
                                <div className="alert alert-warning mt-3 mb-0 py-2">
                                    {conversionBlockedMessage}
                                </div>
                            )}
                        </div>
                        <div className="col-md-4">
                            <div className="border rounded p-3" style={{ backgroundColor: '#f8f9fa' }}>
                                <div className="d-flex justify-content-between mb-2">
                                    <span>Subtotal:</span>
                                    <strong>{formatCurrency(calculateSubtotal())}</strong>
                                </div>
                                {quote.discount && parseFloat(quote.discount) > 0 && (
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Discount:</span>
                                        <strong className="text-success">-{formatCurrency(quote.discount)}</strong>
                                    </div>
                                )}
                                {quote.tax && parseFloat(quote.tax) > 0 && (
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>GST/Tax:</span>
                                        <strong>{formatCurrency(quote.tax)}</strong>
                                    </div>
                                )}
                                {quote.shipping_total && parseFloat(quote.shipping_total) > 0 && (
                                    <div className="d-flex justify-content-between mb-2">
                                        <span>Shipping:</span>
                                        <strong>{formatCurrency(quote.shipping_total)}</strong>
                                    </div>
                                )}
                                <hr className="my-2" />
                                <div className="d-flex justify-content-between">
                                    <span className="fw-bold">Total:</span>
                                    <strong className="text-primary fs-5">{formatCurrency(quote.total)}</strong>
                                </div>
                            </div>
                        </div>
                    </div>

                    {hasLinkedOrder && (
                        <div className="mb-4 pb-4 border-bottom">
                            <h6 className="mb-3">Linked Order</h6>
                            <div className="d-flex gap-2 flex-wrap">
                                <a
                                    href={quote.converted_order_view_url}
                                    className="btn btn-outline-primary"
                                >
                                    <i className="fas fa-eye me-2"></i>
                                    View Order
                                </a>
                                {quote.converted_order_pay_url && (
                                    <a
                                        href={quote.converted_order_pay_url}
                                        className="btn btn-primary"
                                    >
                                        <i className="fas fa-credit-card me-2"></i>
                                        Pay Order
                                    </a>
                                )}
                            </div>
                        </div>
                    )}

                    {quote.fulfillment_type === 'pickup' && (
                        <div className="mb-4 pb-4 border-bottom">
                            <h6 className="mb-3">Pickup</h6>
                            <div className="border rounded p-3">
                                {pickupLines.length > 0 ? (
                                    pickupLines.map((line) => (
                                        <div key={line}>{line}</div>
                                    ))
                                ) : (
                                    <div className="text-muted">No pickup location selected yet.</div>
                                )}
                            </div>
                        </div>
                    )}

                    {quote.fulfillment_type !== 'pickup' && (
                        <div className="mb-4 pb-4 border-bottom">
                            <h6 className="mb-3">Shipping</h6>
                            <div className="row g-3">
                                <div className="col-md-6">
                                    <div className="border rounded p-3 h-100">
                                        <strong className="d-block mb-2">Selected method</strong>
                                        {(quote.shipping_lines && quote.shipping_lines.length > 0) ? quote.shipping_lines.map((shippingLine) => (
                                            <div key={shippingLine.id || shippingLine.rate_id || shippingLine.method_title}>
                                                <div className="fw-semibold">{shippingLine.method_title || 'Shipping'}</div>
                                                <div className="text-muted small">
                                                    {shippingLine.rate_id || quote.selected_shipping_rate_id || shippingLine.method_id || ''}
                                                </div>
                                                <div>{formatCurrency(shippingLine.total || quote.shipping_total)}</div>
                                            </div>
                                        )) : (
                                            <div className="text-muted">No shipping method selected yet.</div>
                                        )}
                                    </div>
                                </div>
                                <div className="col-md-6">
                                    <div className="border rounded p-3 h-100">
                                        <strong className="d-block mb-2">Shipping address</strong>
                                        {shippingAddressLines.length > 0 ? (
                                            shippingAddressLines.map((line) => (
                                                <div key={line}>{line}</div>
                                            ))
                                        ) : (
                                            <div className="text-muted">No shipping address snapshot stored.</div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Notes Section */}
                    {quote.notes && (
                        <div className="mb-4 pb-4 border-bottom">
                            <h6 className="mb-2">Notes</h6>
                            <div className="bg-light p-3 rounded">
                                {quote.notes}
                            </div>
                        </div>
                    )}

                    {/* Line Items */}
                    {quote.line_items && quote.line_items.length > 0 && (
                        <div className="mb-4 pb-4 border-bottom">
                            <h6 className="mb-3">Line Items</h6>
                            <div className="table-responsive">
                                <table className="table table-sm table-bordered">
                                    <thead className="table-light">
                                        <tr>
                                            <th>SKU</th>
                                            <th>Product</th>
                                            <th className="text-center">Qty</th>
                                            <th className="text-end">Unit Price</th>
                                            {quote.line_items.some(item => item.discount && parseFloat(item.discount) > 0) && (
                                                <th className="text-end">Discount</th>
                                            )}
                                            <th className="text-end">Total</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {quote.line_items.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.sku}</td>
                                                <td>{item.product_name}</td>
                                                <td className="text-center">{item.quantity}</td>
                                                <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                {quote.line_items!.some(i => i.discount && parseFloat(i.discount) > 0) && (
                                                    <td className="text-end">
                                                        {item.discount && parseFloat(item.discount) > 0 
                                                            ? formatCurrency(item.discount)
                                                            : '-'}
                                                    </td>
                                                )}
                                                <td className="text-end">{formatCurrency(item.total)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    {(!canEditQuotePermission || !canCheckoutQuotePermission) && (
                        <div className="alert alert-info" role="status">
                            {[
                                !canEditQuotePermission ? 'Editing quotes is disabled.' : '',
                                !canCheckoutQuotePermission ? 'Quote checkout is disabled.' : '',
                            ].filter(Boolean).join(' ')}
                        </div>
                    )}
                    <div className="d-flex gap-2 justify-content-end">
                        {canEdit && (
                            <Link 
                                to={`/quotes/${quote.id}/edit`}
                                className="btn btn-outline-primary"
                            >
                                <i className="fas fa-edit me-2"></i>
                                Edit Quote
                            </Link>
                        )}
                        {hasLinkedOrder && !quote.converted_order_pay_url && (
                            <a
                                className="btn btn-outline-secondary"
                                href={quote.converted_order_view_url}
                            >
                                <i className="fas fa-receipt me-2"></i>
                                View Order
                            </a>
                        )}
                                {canPayQuote && (
                                    <button
                                        className="btn btn-success"
                                        onClick={handlePayQuote}
                                        disabled={completing}
                            >
                                {completing ? (
                                    <>
                                        <span className="spinner-border spinner-border-sm me-2"></span>
                                        Processing...
                                    </>
                                ) : (
                                    <>
                                        <i className="fas fa-credit-card me-2"></i>
                                        {getQuotePrimaryActionLabel(quote)}
                                    </>
                                )}
                            </button>
                        )}
                        {!canPayQuote && conversionBlockedMessage !== '' && (
                            <button
                                className="btn btn-success"
                                disabled
                                title={conversionBlockedMessage}
                            >
                                <i className="fas fa-credit-card me-2"></i>
                                Convert Quote
                            </button>
                        )}
                    </div>
        </MyAccountPageShell>
    );
};

export default ViewQuote;
